<template>
  <div>
    <div
      v-if="readyToProgress && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.meetingCriteria')"
        class="q-mb-md"
      >
        <template #action>
          <div class="row items-center q-col-gutter-md">
            <div>
              <q-btn
                :label="$t('run.collectData.proceedWithCriteria')"
                flat
                color="dark"
                @click="onProceed"
              />
            </div>
            <div>
              <q-btn
                icon="close"
                flat
                :label="$q.platform.is.mobile ? $t('common.buttonDismiss') : null"
                :round="$q.platform.is.mobile"
                color="dark"
                @click="onDismissMastering"
              >
                <q-tooltip
                  v-if="!$q.platform.is.mobile"
                >
                  {{ $t('common.buttonDismiss') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
        </template>
      </banner>
    </div>
    <div
      :class="{
        'no-wrap': dense && tiny,
        'q-col-gutter-sm': dense && tiny,
        'q-col-gutter-md': !dense || !tiny
      }"
      class="row"
    >
      <span
        v-if="readyToProgress && tiny"
      >
        <q-card
          class="col-auto column full-height noselect cursor-pointer"
          @click="onProceedDialog"
        >
          <q-card-section
            class="col row items-center text-center"
          >
            <q-btn
              icon="emoji_events"
              color="positive"
              flat
            />
          </q-card-section>
        </q-card>
      </span>
      <span
        class="items-end row"
      >
        <q-card
          class="col-auto column full-height noselect"
          :class="{'bg-yellow-2': currentMode === 'EO',
                   'bg-green-2': currentMode === 'SR'}"
        >
          <q-card-section
            class="col row items-center text-center"
          >
            <q-badge
              floating
            >
              {{ $t('run.collectData.textCurrentTrial', { currentTrial }) }}
            </q-badge>
            <div
              v-if="!tiny"
              class="col-12 text-dark-variant text-overline text-uppercase"
            >
              {{ $t('run.collectData.textCurrentMode', { currentMode }) }}
            </div>
            <div class="col-12 q-py-xs">
              {{ currentMode }}
            </div>
            <div class="col-12">
              <q-btn-toggle
                v-model="challengeMode"
                color="white"
                text-color="dark"
                toggle-color="dark-variant"
                :disable="disable"
                :dense="!isPinned"
                spread
                unelevated
                outlined
                no-caps
                size="sm"
                :options="challengeModes"
              />
            </div>
          </q-card-section>
        </q-card>
      </span>
      <span
        v-for="target in currentTasks"
        :key="target.id"
      >
        <target-data
          ref="targets"
          :session-program="sessionProgram"
          :target="target"
          :data="data"
          :active-prompt-levels="activePromptLevels"
          :dense="dense"
          :tiny="tiny"
          :disable="disable"
          :workflow-statuses="workflowStatuses"
          :prevent-duplicates-on-trial="currentTrial"
          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"
          @record="onRecordData"
          @record-program-data="onRecordProgramData"
        />
      </span>
      <span
        class=" items-end row"
      >
        <q-card
          class="col-auto column full-height noselect"
          :class="{
            'cursor-pointer': (nextMode === 'SR') && !disable,
            'text-grey-7 cursor-not-allowed': disable,
            'bg-yellow-2': nextMode === 'EO',
            'bg-green-2': nextMode === 'SR'}"
          @click="onSwitchMode"
        >
          <q-card-section
            class="col row items-center text-center q-px-md"
          >
            <div class="col-12">
              {{ nextLabel }}
            </div>
            <template
              v-if="nextMode === 'EO'"
            >
              <div
                class="row col-12 text-caption text-center"
              >
                <div class="col-12">
                  {{ nextTaskLabel }}
                </div>
                <div
                  v-if="nextSubTaskLabel"
                  class="col-12"
                >
                  {{ nextSubTaskLabel }}
                </div>
              </div>
            </template>
          </q-card-section>
        </q-card>
      </span>
    </div>
  </div>
</template>
<script>
import TargetData from 'components/Programs/DataCollection/TargetData';
import Banner from 'components/UI/Banner';
import { Timestamp } from 'firebase/firestore';
import {
  chain, concat, every, filter, find, get, includes, isEmpty, last, map, merge, omit, reject, size, some,
} from 'lodash';
import { mapState } from 'pinia';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionsDB from 'src/fb/sessions-db';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import { sessions } from 'src/store/sessions';

export default {

  name: 'SbtProgram',

  components: { TargetData, Banner },

  mixins: [DataCollectionProgram, Interactions, Targets],

  emits: ['record-program-data', 'record', 'refresh-targets', 'record-multiple-data'],

  data() {
    return {
      proposedTargetChanges: [],

      nextTasks: [],

      targetResponse: null,

    };
  },

  computed: {
    ...mapState(sessions, ['activeSession']),

    isPinned() {
      return get(this.sessionProgram, 'pinned', false);
    },

    challengeModes() {
      const maxChars = this.isPinned ? 1 : 20;
      return [
        {
          value: 'easy',
          coopRatio: 0.4,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.easy').slice(0, maxChars),
        },
        {
          value: 'normal',
          coopRatio: 0.6,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.normal').slice(0, maxChars),
        },
        {
          value: 'challenging',
          coopRatio: 0.8,
          targetRatio: 0.6667,
          label: this.$t('run.collectData.sbtModes.challenging').slice(0, maxChars),
        },
      ];
    },

    hasAutoProgression() {
      return get(this.sessionProgram, ['program', 'sbtAutoProgression'], false);
    },

    sbtTrialsToSucceed() {
      return get(this.sessionProgram, ['program', 'sbtTrialsToSucceed'], 3);
    },

    programPromptLevelTemplate() {
      return find(this.activePromptLevels, { id: this.sessionProgram.program.promptLevelTemplate });
    }, // programPromptLevelTemplate()

    programPrompts() {
      return get(this.programPromptLevelTemplate, 'promptLevels');
    }, // programPrompts()

    defaultPromptLevel() {
      return find(this.programPrompts, { id: get(this.sessionProgram, ['program', 'defaultPromptLevel'], '') });
    }, // defaultPromptLevel()

    currentTrial() {
      return get(this.data, 'currentTrial', 1);
    }, // currentTrial()

    srTargets() {
      return filter(this.sortedTargets, { id: get(this.sessionProgram, ['program', 'srTarget']) });
    }, // srTargets()

    eoTargets() {
      return reject(this.sortedTargets, { id: get(this.sessionProgram, ['program', 'srTarget']) });
    }, // eoTargets()

    cabBranches() {
      return filter(this.sortedTargets, { dataType: 'cabBranch' });
    }, // cabBranches()

    currentTasks() {
      if (this.currentMode === 'SR') {
        return this.srTargets;
      }
      return this.eoTasks;
    },

    eoTasks() {
      if (this.currentEOTarget === null) {
        return [];
      }

      if (this.currentCABBranch === null) {
        // Not in a branch
        const currentTargetOrder = chain(this.sortedTargets)
          .find({ id: this.currentEOTarget })
          .get('order', 0)
          .value();
        return chain(this.eoTargets)
          .reject({ dataType: 'cabBranch' })
          .filter((target) => target.order <= currentTargetOrder)
          .defaultTo([])
          .value();
      }

      const eoSubTarget = (this.currentCABBranchTarget?.subTargets || [])
        .find((target) => target?.id === this.currentEOTarget);

      // Only surface the branch when its active sub-target has something to
      // score. Otherwise the branch renders as a dead, unclickable card.
      const showBranch = eoSubTarget && this.hasScoreableContent(eoSubTarget);

      return chain(this.eoTargets)
        .reject({ dataType: 'cabBranch' })
        .concat(showBranch ? [{
          ...this.currentCABBranchTarget,
          subTargets: [eoSubTarget],
        }] : [])
        .defaultTo([])
        .value();
    },

    challengeMode: {
      get() {
        return get(this.data, 'challengeMode', 'normal');
      },
      set(challengeMode) {
        this.$emit('record-program-data', this.sessionProgram, { challengeMode });
      },
    },

    currentMode() {
      return get(this.data, 'currentMode', 'SR');
    },

    nextMode() {
      return this.currentMode === 'SR' ? 'EO' : 'SR';
    },

    currentCABBranch() {
      return get(this.data, 'currentCABBranch', null);
    },

    currentCABBranchTarget() {
      return find(this.eoTargets, { id: this.currentCABBranch });
    },

    currentEOTarget() {
      return get(this.data, 'currentEOTarget', null);
    },

    nextLabel() {
      if (this.nextMode === 'EO') {
        return this.$t('run.collectData.textNextMode', { nextMode: this.nextMode });
      }
      return this.$t('run.collectData.textSwitchMode', { nextMode: this.nextMode });
    }, // nextLabel()

    nextTaskLabel() {
      return this.getTargetLabel(last(this.eoTasks));
    }, // nextTaskLabel()

    nextSubTaskLabel() {
      const targetTask = last(this.eoTasks);

      if (get(targetTask, 'dataType') === 'cabBranch') {
        return this.getTargetLabel(
          chain(targetTask)
            .get('subTargets')
            .last()
            .value(),
        );
      }

      return null;
    }, // nextSubTaskLabel()

    readyToProgress() {
      return (!isEmpty(this.proposedTargetChanges)) && (this.currentMode === 'SR');
    },

  },
  watch: {
    sortedTargets: {
      immediate: false,
      deep: true,
      handler() {
        if (!this.disable && (this.currentMode === 'SR')) {
          const programData = this.getNewTargetEOTask();
          this.$emit('record-program-data', this.sessionProgram, programData);
        }
      },
    },
  },

  created() {
    if (this.currentEOTarget === null) {
      const programData = {
        ...this.getNewTargetEOTask(),
      };
      this.$emit('record-program-data', this.sessionProgram, programData);
    }
  },

  methods: {

    finishSession() {
      let finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession();
          }
          return null;
        })
        .flatten()
        .compact()
        .map((trialData) => ({
          ...trialData,
          trial: this.currentTrial,
          targetResponse: this.targetResponse,
        }))
        .value();

      let skippedTrials = [];

      if (!chain(this.data)
        .get('data')
        .filter({ trial: this.currentTrial })
        .isEmpty()
        .value()) {
        const finalDataTargets = map(finalData, 'target');
        skippedTrials = reject(this.skipTrials(), (trialData) => includes(finalDataTargets, trialData.target));
        finalData = concat(finalData, skippedTrials);
      }
      return {
        finalData,
      };
    }, // finishSession()

    undoData() {
      if (this.disable) {
        return;
      }
      const lastTrial = chain(this.data)
        .get('data')
        .last()
        .value();
      const lastMode = chain(this.srTargets).map('id').includes(get(lastTrial, 'target')).value() ? 'SR' : 'EO';

      if (lastMode === this.currentMode) {
        return true;
      }
      if (lastMode === 'EO') {
        this.notifyNegative(this.$t('run.collectData.notifySbtUndoWarning'));
        return false;
      }
      const programData = {
        currentTrial: get(lastTrial, 'trial', 1),
        currentMode: 'SR',
      };

      this.$emit('record-program-data', this.sessionProgram, programData);
      return true;
    },

    onSwitchMode() {
      if ((this.currentMode === 'SR') || this.disable) {
        return;
      }
      this.switchMode();
    }, // onSwitchMode()

    onRecordData(sessionProgram, target, data) {
      const saveData = {
        ...data,
        trial: this.currentTrial,
        mode: this.challengeMode,
        targetResponse: this.targetResponse,
      };
      this.$emit('record', sessionProgram, target, saveData);

      if (this.currentMode === 'SR') {
        this.switchMode();
      }
    }, // onRecordData()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    },

    onProceedDialog() {
      this.confirmDialog(
        null,
        this.$t('run.collectData.meetingCriteria'),
        () => {
          this.onProceed();
        },
        {
          ok: {
            label: this.$t('run.collectData.proceedWithCriteria'),
            flat: true,
          },
        },
      );
    },

    async onProceed() {
      const sessionId = get(this.activeSession, 'id');
      if (!sessionId) {
        return;
      }
      this.$q.loading.show();
      const proposedTargetChangeTargets = map(this.proposedTargetChanges, 'target');
      const targetStatusChanges = chain(this.activeSession)
        .get('targetStatusChanges', [])
        .reject((targetChange) => includes(proposedTargetChangeTargets, targetChange.target))
        .concat(chain(this.proposedTargetChanges).map((targetChange) => omit(targetChange, 'targetLabel')).value())
        .value();

      const sessionsDb = new SessionsDB();
      sessionsDb.set({ targetStatusChanges }, sessionId);

      const targetUpdatesActions = map(this.proposedTargetChanges, (targetChange) => {
        const programTargetsDb = new ProgramTargetsDB(targetChange.program);
        const targetProperties = { status: targetChange.toStatus };
        if (targetChange.toStatus === 'mastered') {
          targetProperties.masteryDate = Timestamp.now();
        }
        return programTargetsDb.set(targetProperties, targetChange.target);
      });
      await Promise.all(targetUpdatesActions);

      await this.updateTargetsPerStatus(this.sessionProgram.program);

      const targetLabels = chain(this.proposedTargetChanges)
        .filter({ toStatus: 'acquisition' })
        .map('targetLabel')
        .join(', ')
        .value();
      const targetCount = chain(this.proposedTargetChanges)
        .filter({ toStatus: 'acquisition' })
        .size()
        .value();

      this.notifySuccess(this.$tc('run.collectData.proceedSuccess', targetCount, {
        targetLabels,
      }));

      this.proposedTargetChanges = [];
      this.targetResponse = null;

      this.$emit('refresh-targets', get(this.sessionProgram, ['program', 'id']));
      this.$q.loading.hide();
    }, // onProceed()

    onDismissMastering() {
      this.proposedTargetChanges = [];
    }, // onDismissMastering()

    switchMode() {
      let programData = {};
      if (this.currentMode === 'SR') {
        programData = { currentMode: 'EO' };
      } else {
        const skippedTrials = this.skipTrials();

        const previousTrial = chain(this.data).get('data').last().value();
        const currentTrial = get(previousTrial, 'trial', 0);
        if (isEmpty(skippedTrials)) {
          this.checkMasteringCriteria(currentTrial);
        }

        programData = {
          currentTrial: currentTrial + 1,
          currentMode: 'SR',
          ...this.getNewTargetEOTask(),
        };

        if (!isEmpty(skippedTrials)) {
          this.$emit('record-multiple-data', this.sessionProgram, skippedTrials);
        }
      }

      this.$emit('record-program-data', this.sessionProgram, programData);
    }, // switchMode()

    skipTrials() {
      if ((this.currentMode !== 'EO') || !this.defaultPromptLevel) {
        return;
      }
      return chain(this.eoTasks)
        .flatMap((target) => {
          // Flatten out cabBranches
          if (target.dataType === 'cabBranch') {
            return get(target, 'subTargets', []);
          }
          return target;
        })
        .flatMap((target) => {
          if (isEmpty(target.subTargets)) {
            return target;
          }
          if (['shaping'].includes(target.dataType)) {
            // Shaping should not record default data
            // for other than the current shaping-target
            return chain(target).get('subTargets', []).last().value();
          }
          if (['randomRotation'].includes(target.dataType)) {
            // Random rotation should not record default data
            // for other than the current rr-target
            return chain(target)
              .get('subTargets', [])
              .find({ id: get(this.data, `rrTarget_${target.id}`, null) })
              .defaultTo(null)
              .value();
          }

          return get(target, 'subTargets', []);
        })
        .compact()
        // Disable if we already have data for this target at this trial
        .filter((target) => (chain(this.data)
          .get(['data'])
          .filter({ target: target.id })
          .filter({ trial: this.currentTrial })
          .isEmpty()
          .value()))
        .map((target) => {
          const saveSkippedData = {
            target: target.id,
            promptLevel: this.defaultPromptLevel.id,
            scoreValue: this.defaultPromptLevel.score,
            trial: this.currentTrial,
            targetResponse: this.targetResponse,
          };
          return saveSkippedData;
        })
        .value();
    }, // skipTrials()

    checkMasteringCriteria(currentTrial) {
      if (this.disable || !this.hasAutoProgression) {
        return;
      }

      const targetResponse = chain(this.data)
        .get('data')
        .filter({ trial: currentTrial })
        .last()
        .get('targetResponse', null)
        .value();

      if (targetResponse === null) {
        return;
      }

      const successCriteria = 100;
      const previousTrialsOutcome = chain(this.data)
        .get('data')
        .filter({ targetResponse })
        .groupBy('trial')
        // Don't count skipped trials:
        .reject((trialData) => (some(trialData, { scoreValue: null })))
        .map((trialData, trial) => ({
          trial: parseInt(trial, 10),
          isMastered: chain(trialData).meanBy('scoreValue').gte(successCriteria).value(),
        }))
        .sortBy('trial')
        .slice(-this.sbtTrialsToSucceed)
        .map('isMastered')
        .value();

      if ((size(previousTrialsOutcome) >= this.sbtTrialsToSucceed) && (every(previousTrialsOutcome))) {
        let targetResponseTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .find({ id: targetResponse })
          .defaultTo(null)
          .value();
        // RR, TA, Shaping targets - find the last open target within our target response
        const lastSubTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .filter({ parentTarget: targetResponse })
          .reject({ status: 'waiting' })
          .last()
          .defaultTo(null)
          .value();

        if (lastSubTarget !== null) {
          targetResponseTarget = lastSubTarget;
        }

        if (targetResponseTarget === null || targetResponseTarget?.status === 'mastered') {
          return;
        }

        // Check what to open next.
        // Priority:
        // Open next target with same parent target

        // Master current target
        // If no targets then recurse up and open next on upper level
        // If next target is a CAB branch then open all CAB branches that are waiting
        // Recurse down and open first target on targets (if any)
        this.masterTarget(targetResponseTarget);
      }
    }, // checkMasteringCriteria()

    openTarget(target) {
      this.addTargetChange({
        program: this.sessionProgram.program.id,
        target: target.id,
        targetLabel: target.label,
        fromStatus: target.status,
        toStatus: 'acquisition',
      });

      if (target.dataType === 'cabBranch') {
        // Check if it's a CAB Branch and then open other CAB Branches at same level
        const nextTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
          .filter({ parentTarget: get(target, 'parentTarget', null) })
          .filter({ status: 'waiting' })
          .reject((targetObj) => targetObj.order <= target.order)
          .sortBy('order')
          .head()
          .value();
        if (get(nextTarget, 'dataType') === 'cabBranch') {
          this.openTarget(nextTarget);
        }
      }

      const subTarget = chain(this.sessionProgram)
        .get(['program', 'targets'])
        .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
        .filter({ parentTarget: get(target, 'id', null) })
        .filter({ status: 'waiting' })
        .sortBy('order')
        .head()
        .value();

      if (!isEmpty(subTarget)) {
        this.openTarget(subTarget);
      }
    }, // openTarget()

    masterTarget(target) {
      this.addTargetChange({
        program: this.sessionProgram.program.id,
        target: target.id,
        targetLabel: target.label,
        fromStatus: target.status,
        toStatus: 'mastered',
      });

      const allTargetsAtLevelMastered = chain(this.sessionProgram)
        .get(['program', 'targets'])
        .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
        .reject({ id: target.id })
        .filter({ parentTarget: get(target, 'parentTarget', null) })
        .reject({ status: 'mastered' })
        .reject({ status: 'closed' })
        .isEmpty()
        .value();

      if (allTargetsAtLevelMastered) {
        const parentTarget = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .find({ id: target.parentTarget })
          .value();
        if (!isEmpty(parentTarget)) {
          this.masterTarget(parentTarget);
        }
      } else {
        const openTargetSameLevel = chain(this.sessionProgram)
          .get(['program', 'targets'])
          .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
          .filter({ parentTarget: get(target, 'parentTarget', null) })
          .filter({ status: 'waiting' })
          .sortBy('order')
          .head()
          .value();

        if (!isEmpty(openTargetSameLevel)) {
          this.openTarget(openTargetSameLevel);
        }
      }
    }, // masterTarget()

    addTargetChange(newTargetChange) {
      if (find(this.proposedTargetChanges, { target: newTargetChange.target })) {
        this.proposedTargetChanges = chain(this.proposedTargetChanges)
          .map((targetChange) => {
            if (targetChange.target === newTargetChange.target) {
              return {
                ...targetChange,
                toStatus: newTargetChange.toStatus,
              };
            }
            return targetChange;
          })
          .value();
      } else {
        this.proposedTargetChanges = concat(this.proposedTargetChanges, newTargetChange);
      }
    },

    getNewTargetEOTask() {
      const branchIndex = Math.floor(Math.random() * size(this.cabBranches));
      let currentCABBranch = get(this.cabBranches, [branchIndex, 'id'], null);

      const flatTargetList = chain(this.sortedTargets)
        .reject({ dataType: 'cabBranch' })
        .reject({ id: get(this.sessionProgram, ['program', 'srTarget']) })
        .concat(
          chain(this.sortedTargets)
            .find({ id: currentCABBranch })
            .get('subTargets')
            .reject({ status: 'mastered' })
            .value(),
        )
        .compact()
        // Skip targets that have nothing to present (e.g. a randomRotation
        // whose sub-targets are all waiting/hidden) so they never become the
        // active EO task and render as an empty, unclickable card.
        .filter((target) => this.hasScoreableContent(target))
        .value();

      const nextTargetIdx = this.getRandomTargetIndex(size(flatTargetList));

      const currentEOTarget = get(flatTargetList, [nextTargetIdx, 'id'], null);

      if (nextTargetIdx === (size(flatTargetList) - 1)) {
        if (get(flatTargetList, [nextTargetIdx, 'dataType']) === 'shaping') {
          this.targetResponse = chain(flatTargetList)
            .get([nextTargetIdx, 'subTargets'])
            .last()
            .get('id', null)
            .value();
        } else {
          this.targetResponse = currentEOTarget;
        }
      } else {
        this.targetResponse = null;
      }
      if (get(flatTargetList, [nextTargetIdx, 'parentTarget'], null) === null) {
        // EO Target is not in a CAB
        currentCABBranch = null;
      }

      // Rotate RR targets
      const rrTargetUpdates = chain(flatTargetList)
        .filter({ dataType: 'randomRotation' })
        .map((target) => (this.rotateTarget(target)))
        .reduce((res, val) => merge(res, val), {})
        .value();

      const rrTargetResponse = get(rrTargetUpdates, `rrTarget_${this.targetResponse}`, null);
      if (rrTargetResponse) {
        // Current target response is an RR target. Let's check if it's already
        // mastered - in that case it's not our target response and we should reset
        // it. Otherwise it is and we should aim for that as our target response
        if (['mastered', 'closed'].includes(chain(this.sessionProgram)
          .get(['program', 'targets'])
          .find({ id: rrTargetResponse })
          .get('status')
          .value())) {
          this.targetResponse = null;
        } else {
          this.targetResponse = rrTargetResponse;
        }
      }
      return {
        currentEOTarget,
        currentCABBranch,
        ...rrTargetUpdates,
      };
    }, // getNewTargetEOTask()

    // Get a random number that's biased towards the lower or higher end
    // If challengeLevel < 0 it will be biased towards the lower end
    // If challengeLevel == 0 it will be equally distributed
    // If challengeLevel > 0 it will be biased towards the higher end
    getRandomTargetIndex(numTargets) {
      const nonCoopTargetCount = 2;

      const coopRatio = chain(this.challengeModes)
        .find({ value: this.challengeMode })
        .get('coopRatio', 0)
        .value();
      const targetRatio = chain(this.challengeModes)
        .find({ value: this.challengeMode })
        .get('targetRatio', 0)
        .value();

      // We assume that the first two targets are non-Coop targets
      // so if we are not yet working with coop-targets we will
      // just return a random target
      if (numTargets <= nonCoopTargetCount) {
        return Math.floor(Math.random() * numTargets);
      }

      // If we have started coop the algorithm (in normal mode) is:
      // 60% chance we are picking a Coop target and 40% chance (of all)
      // that we are picking the current (max) target

      if (Math.random() >= coopRatio) {
        // We did not get a coop target - equal chance for Comms/TR targets
        return Math.floor(Math.random() * nonCoopTargetCount);
      }

      if (numTargets === (nonCoopTargetCount + 1)) {
        // We got a coop target and there is only one coop target available
        return numTargets - 1;
      }
      if (Math.random() < targetRatio) {
        // We got coop and current target - return last possible target
        return numTargets - 1;
      }

      // Select a random coop target that's not the current target
      return Math.floor(
        Math.random()
        * (numTargets - (nonCoopTargetCount + 1)),
      ) + nonCoopTargetCount;
    }, // getRandomTargetIndex()

    // A randomRotation with no displayable sub-targets has nothing to rotate
    // to, so it renders an empty, unclickable card. Treat it as not scoreable.
    hasScoreableContent(target) {
      if (get(target, 'dataType') === 'randomRotation') {
        return !isEmpty(get(target, 'subTargets', []));
      }
      return true;
    }, // hasScoreableContent()

    getTargetLabel(target) {
      if (get(target, 'dataType') === 'randomRotation') {
        return chain(target)
          .get('subTargets', [])
          .find({ id: get(this.data, `rrTarget_${target.id}`, null) })
          .get('label', null)
          .value();
      }
      return get(target, 'label', null);
    },

  },
};
</script>
